import { create } from 'zustand';
import { api, ApiError, clearTokens, readAccessToken, setRefreshHandler, writeTokens } from '../lib/api';
import { saveTerminalId } from '../lib/terminal';
import { useCart } from './cart';
import { useSaleAttempt } from './sale-attempt';
import type { BusinessProfileInfo, InsightSettings, LoginResult, MeResult, Principal } from '../lib/types';

/** Used until the session says otherwise (and by an API older than the setting). */
const DEFAULT_INSIGHTS: InsightSettings = { costAlertBps: 300 };

interface AuthState {
  principal: Principal | null;
  store: MeResult['store'];
  /** true while the first `/auth/me` is in flight, so the shell can hold back the UI */
  initialising: boolean;
  signingIn: boolean;
  error: string | null;
  permissions: Set<string>;
  /** Business type and its switched-on modules; null when the API does not say (everything is on). */
  businessProfile: BusinessProfileInfo | null;
  insightSettings: InsightSettings;

  signIn: (username: string, password: string, terminalId?: string) => Promise<boolean>;
  signOut: () => void;
  restore: () => Promise<void>;
  can: (permission: string) => boolean;
  /** Whether this business runs a module. An unknown profile (older API) counts as on. */
  hasModule: (code: string) => boolean;
  /** Re-read the profile after the superadmin changes it, so the menu follows at once. */
  refreshProfile: () => Promise<void>;
  canAny: (...permissions: string[]) => boolean;
}

async function refreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem('sp.refreshToken');
  if (!refreshToken) return false;
  try {
    const result = await api.post<LoginResult>('/api/auth/refresh', { refreshToken });
    writeTokens(result.tokens.accessToken, result.tokens.refreshToken);
    return true;
  } catch {
    return false;
  }
}

export const useAuth = create<AuthState>((set, get) => ({
  principal: null,
  store: null,
  initialising: true,
  signingIn: false,
  error: null,
  permissions: new Set<string>(),
  businessProfile: null,
  insightSettings: DEFAULT_INSIGHTS,

  async signIn(username, password, terminalId) {
    set({ signingIn: true, error: null });
    try {
      const result = await api.post<LoginResult>('/api/auth/login', {
        username,
        password,
        ...(terminalId ? { terminalId } : {}),
      });
      const pending = useSaleAttempt.getState().pending;
      const session = [result.principal.userId, result.principal.storeId, terminalId ?? result.principal.terminalId].join(':');
      if (pending && pending.session !== session) {
        set({ signingIn: false, error: 'Resolve the submitted sale using its original cashier and terminal before changing session.' });
        return false;
      }
      useCart.getState().clear();
      writeTokens(result.tokens.accessToken, result.tokens.refreshToken);
      // The token carries the register the session was opened on, so remember it here: a till
      // that reloads or reopens mid-shift has to find the same drawer without being told again.
      saveTerminalId(terminalId ?? result.principal.terminalId);
      const me = await api.get<MeResult>('/api/auth/me').catch(() => null);
      set({
        principal: result.principal,
        store: me?.store ?? null,
        businessProfile: me?.businessProfile ?? null,
        insightSettings: me?.insightSettings ?? DEFAULT_INSIGHTS,
        permissions: new Set(result.principal.permissions),
        signingIn: false,
        error: null,
      });
      return true;
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Check the connection and try again.';
      set({ signingIn: false, error: message });
      return false;
    }
  },

  signOut() {
    if (useSaleAttempt.getState().pending) return;
    useCart.getState().clear();
    clearTokens();
    set({ principal: null, store: null, permissions: new Set<string>(), businessProfile: null, error: null });
  },

  async restore() {
    if (!readAccessToken()) {
      set({ initialising: false });
      return;
    }
    try {
      const me = await api.get<MeResult>('/api/auth/me');
      set({
        principal: me.user,
        store: me.store,
        permissions: new Set(me.user.permissions),
        businessProfile: me.businessProfile ?? null,
        insightSettings: me.insightSettings ?? DEFAULT_INSIGHTS,
        initialising: false,
      });
    } catch {
      // A dead access token is not a reason to keep a cashier stuck on a spinner.
      clearTokens();
      set({ principal: null, store: null, permissions: new Set<string>(), businessProfile: null, initialising: false });
    }
  },

  can(permission) {
    return get().permissions.has(permission);
  },

  hasModule(code) {
    const profile = get().businessProfile;
    return profile === null || profile.enabledModules.includes(code);
  },

  async refreshProfile() {
    const me = await api.get<MeResult>('/api/auth/me').catch(() => null);
    if (me) set({ businessProfile: me.businessProfile ?? null, insightSettings: me.insightSettings ?? DEFAULT_INSIGHTS });
  },

  canAny(...list) {
    const held = get().permissions;
    return list.some((permission) => held.has(permission));
  },
}));

setRefreshHandler(refreshTokens);

/** Non-hook read, for the rare places that cannot use a hook (an event handler on window). */
export const authCan = (permission: string): boolean => useAuth.getState().permissions.has(permission);
